import React from "react";
import {
  Modal,
  Descriptions,
  Tag,
  Row,
  Col,
  Card,
  Statistic,
  Divider,
  Button,
  Space,
} from "antd";
import {
  CalendarOutlined,
  ShopOutlined,
  InboxOutlined,
  EnvironmentOutlined,
  AlertOutlined,
} from "@ant-design/icons";
import type { StockItem } from "../types/Stock";
import dayjs from "dayjs";

interface StockDetailsModalProps {
  visible: boolean;
  onCancel: () => void;
  stockItem: StockItem | null;
}

const StockDetailsModal: React.FC<StockDetailsModalProps> = ({
  visible,
  onCancel,
  stockItem,
}) => {
  if (!stockItem) {
    return null;
  }

  const getStockStatus = () => {
    if (stockItem.quantity.current === 0) {
      return <Tag color="red">Out of Stock</Tag>;
    } else if (stockItem.quantity.current <= stockItem.quantity.minimum) {
      return <Tag color="orange">Low Stock</Tag>;
    } else {
      return <Tag color="green">In Stock</Tag>;
    }
  };

  const getExpiryStatus = () => {
    const today = dayjs();
    const expiry = dayjs(stockItem.expiryDate);
    const daysToExpiry = expiry.diff(today, "days");

    if (daysToExpiry < 0) {
      return <Tag color="red">Expired</Tag>;
    } else if (daysToExpiry <= 30) {
      return <Tag color="orange">Expires Soon</Tag>;
    } else {
      return <Tag color="green">Valid</Tag>;
    }
  };

  const profitMargin = (
    ((stockItem.pricing.sellingPrice - stockItem.pricing.purchasePrice) /
      stockItem.pricing.purchasePrice) *
    100
  ).toFixed(2);

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <InboxOutlined />
          <span>Stock Item Details</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={
        <Button type="primary" onClick={onCancel}>
          Close
        </Button>
      }
      width={900}
    >
      {/* Header Info */}
      <Card style={{ marginBottom: "16px" }}>
        <Row gutter={16}>
          <Col span={18}>
            <h3 style={{ margin: 0, fontSize: "20px" }}>{stockItem.name}</h3>
            {stockItem.genericName && (
              <p style={{ margin: "4px 0", color: "#666" }}>
                Generic: {stockItem.genericName}
              </p>
            )}
            <div style={{ marginTop: "8px" }}>
              <Space>
                <Tag color="blue">{stockItem.category}</Tag>
                {getStockStatus()}
                {getExpiryStatus()}
                {stockItem.isActive ? (
                  <Tag color="green">Active</Tag>
                ) : (
                  <Tag color="red">Inactive</Tag>
                )}
              </Space>
            </div>
          </Col>
          <Col span={6} style={{ textAlign: "right" }}>
            <Statistic
              title="Current Stock"
              value={stockItem.quantity.current}
              suffix={stockItem.quantity.unit}
              valueStyle={{
                color:
                  stockItem.quantity.current <= stockItem.quantity.minimum
                    ? "#f5222d"
                    : "#3f8600",
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: "16px" }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Purchase Price"
              value={stockItem.pricing.purchasePrice}
              precision={2}
              prefix="₹"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Selling Price"
              value={stockItem.pricing.sellingPrice}
              precision={2}
              prefix="₹"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="MRP"
              value={stockItem.pricing.mrp}
              precision={2}
              prefix="₹"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Profit Margin"
              value={profitMargin}
              precision={2}
              suffix="%"
              valueStyle={{
                color: parseFloat(profitMargin) > 0 ? "#3f8600" : "#f5222d",
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Detailed Information */}
      <Card>
        <Descriptions title="Product Information" bordered column={2}>
          <Descriptions.Item label="Medicine ID">
            {stockItem.medicineId}
          </Descriptions.Item>
          <Descriptions.Item label="Batch Number">
            {stockItem.batchNumber}
          </Descriptions.Item>
          <Descriptions.Item label="Manufacturer">
            <Space>
              <ShopOutlined />
              {stockItem.manufacturer}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Supplier">
            {stockItem.supplier.name}
            <br />
            <small style={{ color: "#666" }}>
              {stockItem.supplier.contactPerson} - {stockItem.supplier.phone}
            </small>
          </Descriptions.Item>
          <Descriptions.Item label="Manufacturing Date">
            <Space>
              <CalendarOutlined />
              {dayjs(stockItem.manufacturingDate).format("DD/MM/YYYY")}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Expiry Date">
            <Space>
              <CalendarOutlined />
              {dayjs(stockItem.expiryDate).format("DD/MM/YYYY")}
              <span style={{ color: "#666" }}>
                ({dayjs(stockItem.expiryDate).diff(dayjs(), "days")} days left)
              </span>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Minimum Stock Level">
            <Space>
              <AlertOutlined style={{ color: "#faad14" }} />
              {stockItem.quantity.minimum} {stockItem.quantity.unit}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Stock Location">
            {stockItem.location?.rack ||
            stockItem.location?.shelf ||
            stockItem.location?.position ? (
              <Space>
                <EnvironmentOutlined />
                <div>
                  {stockItem.location?.rack &&
                    `Rack: ${stockItem.location.rack}`}
                  {stockItem.location?.shelf &&
                    ` | Shelf: ${stockItem.location.shelf}`}
                  {stockItem.location?.position &&
                    ` | Position: ${stockItem.location.position}`}
                </div>
              </Space>
            ) : (
              <span style={{ color: "#999" }}>Not specified</span>
            )}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        <Descriptions title="Pricing Details" bordered column={2}>
          <Descriptions.Item label="Purchase Price">
            ₹{stockItem.pricing.purchasePrice.toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Selling Price">
            ₹{stockItem.pricing.sellingPrice.toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="MRP">
            ₹{stockItem.pricing.mrp.toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Discount">
            {stockItem.pricing.discount}%
          </Descriptions.Item>
          <Descriptions.Item label="Tax Rate">
            {stockItem.pricing.taxPercent ?? 0}%
          </Descriptions.Item>
          <Descriptions.Item label="Supplier Tax Amount">
            ₹{(stockItem.pricing.tax ?? 0).toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Profit per Unit">
            ₹
            {(
              stockItem.pricing.sellingPrice - stockItem.pricing.purchasePrice
            ).toFixed(2)}
          </Descriptions.Item>
          <Descriptions.Item label="Total Stock Value">
            ₹
            {(
              stockItem.quantity.current * stockItem.pricing.purchasePrice
            ).toFixed(2)}
          </Descriptions.Item>
        </Descriptions>

        {stockItem.notes && (
          <>
            <Divider />
            <Descriptions title="Additional Notes" bordered>
              <Descriptions.Item label="Notes" span={3}>
                {stockItem.notes}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}

        <Divider />

        <Descriptions title="System Information" bordered column={2}>
          <Descriptions.Item label="Created At">
            {dayjs(stockItem.createdAt).format("DD/MM/YYYY HH:mm:ss")}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {dayjs(stockItem.updatedAt).format("DD/MM/YYYY HH:mm:ss")}
          </Descriptions.Item>
          <Descriptions.Item label="Low Stock Alert">
            {stockItem.alertLowStock ? (
              <Tag color="orange">Enabled</Tag>
            ) : (
              <Tag>Disabled</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {stockItem.isActive ? (
              <Tag color="green">Active</Tag>
            ) : (
              <Tag color="red">Inactive</Tag>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </Modal>
  );
};

export default StockDetailsModal;
